
/**
 * TODO:
 * 現状は各情報を都度fetchしているが、「現在のポイント」情報などをストアにキャッシュする機構を実装する
 */

import { computed, defineComponent, onMounted, reactive, ref } from 'vue';
import { useDialogWithRouter } from '@/compositions/useDialogWithRouter';
import PurchaseCoupon from '@/components/organisms/coupon/PurchaseCoupon.vue';
import { useCoupon } from '@/compositions/useCoupon';
import { Coupon as CouponType } from '@/repositories/couponRepository';
import axios from 'axios';
import { useRouter } from 'vue-router/composables';

type State = {
  isLoading: boolean;
  step: 'CONFIRM' | 'COMPLETE' | 'FAILED';
  initialized: boolean;
  statusCode?: number;
  restricted: boolean;
};

type Props = {
  coupon: CouponType;
};

export default defineComponent<Props>({
  name: 'DialogCouponPurchaseConfirm',

  components: {
    PurchaseCoupon,
  },

  props: {
    coupon: {
      type: Object,
      required: true,
    },
  },

  setup(props: Props, { emit }) {
    const router = useRouter();
    const { dialog, open, close, afterClose } = useDialogWithRouter(router);
    const { purchaseInfoGenerate, canExchange } = useCoupon();

    const state = reactive<State>({
      isLoading: false,
      step: 'CONFIRM',
      initialized: false,
      statusCode: undefined,
      restricted: false,
    });

    /**
     * 交換可能かどうか
     */
    const exchangable = computed(() => {
      return true;
    });

    /**
     * 交換制御されているかどうか
     */
    const restrictedCoupon = async () => {
      const result = await canExchange(props.coupon);
      state.restricted = !result.data.exchangable;
    };

    /**
     * クーポンを購入する（競合が発生した場合にリトライ）
     */
    const purchaseCoupon = async () => {
      state.isLoading = true;

      // Retries with Exponential Backoff
      const maxRetries = 3;
      const delayMs = 500;
      for (let count = 1; count <= maxRetries; count++) {
        try {
          // クーポン購入
          const {
            stripeSession,
          }: any /*{ stripeSession: Stripe.Response<Stripe.Checkout.Session> }*/ = await purchaseInfoGenerate(
            props.coupon.id
          );

          if (!stripeSession.url) {
            throw new Error(`stripeSession.url is not found. stripeSession: ${JSON.stringify(stripeSession)}`);
          }

          window.location.href = stripeSession.url;

          state.step = 'COMPLETE';
          emit('exchange'); // 交換完了イベントを送出して、クーポン一覧を再読み込みする
          return;
        } catch (e) {
          console.error(e);
          if (!axios.isAxiosError(e)) throw e;

          if (e.response) {
            switch (e.response.status) {
              case 409: // 競合発生時にリトライ
                console.warn(`409 Conflict 発生 (count ${count}/${maxRetries})。再試行します...`);
                if (count < maxRetries) {
                  await new Promise(resolve => setTimeout(resolve, delayMs * Math.pow(2, count - 1)));
                  continue; // リトライ
                } else {
                  console.error('最大リトライ回数に達しました。');
                  throw new Error('競合が解決しませんでした。時間をおいて再試行してください。');
                }
              case 429:
                router.push('/error429');
                return;
              case 404:
                router.push('/error404');
                return;
              case 419: // 在庫なし
                emit('empty-stock-coupon', props.coupon.id);
                return;
              case 420: // 交換制限
                emit('restricted', props.coupon.id);
                return;
              default:
                throw e; // 想定外のエラーはそのままスロー
            }
          }
        }
      }

      state.step = 'FAILED';
      state.isLoading = false;
    };

    onMounted(async () => {
      open();
      state.initialized = true;
      restrictedCoupon();
    });

    return {
      // dialog
      dialog,
      close,
      afterClose: () => {
        afterClose();
        emit('close');
      },

      state,
      exchangable,
      purchaseCoupon,
      restrictedCoupon,
    };
  },
});
